import { v4 } from 'uuid'
import * as Compress from 'client-compress'

import UploadConstraintValidator from './UploadConstraintValidator'

class UploadDirector implements Upload.Director {
  config: Upload.DirectorConfig
  store: Upload.DirectorStore = []

  constructor(config: Upload.DirectorConfig) {
    this.config = {
      type: 'singleFileInput',
      allowMultiple: false,
      ...config,
    }
  }

  public getConstraints(): Upload.DirectorConstraints {
    return this.config.constraints
  }

  public async addFileList(fileList: FileList): Promise<Upload.DirectorAddFilesResponse> {
    const list = Array.from(fileList)

    const files = await Promise.all(
      list.map(async (file: File) => {
        let data: any = file
        const compressionEnabled = this.config?.compression?.enabled
        const isImage = file.type.split('/')[0] === 'image'
        // If a file is in our allowed MIME types it can be compressed
        const canBeCompressed = this.config?.constraints.allowedMimeTypes.includes(file.type)

        if (compressionEnabled && isImage && canBeCompressed) {
          try {
            data = await new Promise(async (resolve, reject) => {
              setTimeout(() => reject(file), 5000)
              try {
                const compress = new Compress(this.config?.compression || {})
                const files = await compress.compress([file])
                const { photo } = files[0]
                resolve(photo.data)
              } catch (error) {
                console.error(error)
                reject(file)
              }
            })
          } catch (error) {
            data = file
          }
        }
        // If compression is enabled, we want to designate the type to be that of the compressed file
        const type = compressionEnabled ? data.type : file.type

        return {
          id: this.getFileId(),
          directorId: this.config.id,
          name: file.name,
          type,
          uploadFilename: null,
          data,
        }
      })
    )

    return this.addFiles(files)
  }

  public clear() {
    this.store = []
  }

  public addFiles(files: Upload.DirectorFile[]): Upload.DirectorAddFilesResponse {
    const validator = new UploadConstraintValidator(this.config, files, this.getFilesForDirector())
    const data = validator.validate()

    if (this.isMultiple()) {
      this.store = [...this.store, ...data.files]
    } else {
      this.store = [...data.files]
    }

    return data
  }

  public removeFile(id: string) {
    this.store = this.store.filter((file) => file.id === id)
  }

  public isMultiple(): boolean {
    return this.config.allowMultiple
  }

  public getAllowedMimeTypesAsAcceptAttribute(): string {
    const allowed = this.config?.constraints?.allowedMimeTypes
    return allowed ? allowed.map((mime) => (mime === 'text/csv' ? '.csv' : mime)).join(',') : null
  }

  public hasMaxFiles(): boolean {
    if (this.config.constraints?.maxFiles) {
      return this.getFilesForDirector().length === this.config.constraints.maxFiles
    }

    return false
  }

  private getFilesForDirector() {
    return this.store.filter((file) => file.directorId === this.config.id)
  }

  private getFileId(): string {
    const id = v4()

    if (this.store.find((file) => file.id === id)) {
      return this.getFileId()
    }

    return id
  }
}

export default UploadDirector
